import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { CalendarClock, Plus, Trash2, X, CheckCircle2, XCircle, Clock, Inbox } from "lucide-react";

const SLOT_STATUS = {
  open: { label: "Disponible", cls: "bg-emerald-50 text-emerald-700" },
  pending: { label: "Demande en attente", cls: "bg-amber-50 text-amber-700" },
  booked: { label: "Réservé", cls: "bg-sky-50 text-sky-700" },
};
const BOOK_STATUS = {
  pending: { label: "En attente", cls: "bg-amber-50 text-amber-700" },
  accepted: { label: "Acceptée", cls: "bg-emerald-50 text-emerald-700" },
  refused: { label: "Refusée", cls: "bg-rose-50 text-rose-700" },
};

export default function AgendaExpert() {
  const [expert, setExpert] = useState(undefined);
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), start_time: "09:00", end_time: "10:00", note: "" });

  const load = () => {
    api.get("/expert/slots").then((r) => setSlots(r.data)).catch(() => {});
    api.get("/expert/bookings").then((r) => setBookings(r.data)).catch(() => {});
  };
  useEffect(() => {
    api.get("/expert/profile").then((r) => setExpert(r.data.expert)).catch(() => setExpert(null));
    load();
  }, []);

  const create = async () => {
    if (!form.date || !form.start_time || !form.end_time) return toast.error("Champs requis");
    try {
      await api.post("/expert/slots", form);
      toast.success("Créneau publié");
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => { await api.delete(`/expert/slots/${id}`); load(); };
  const decide = async (id, status) => {
    await api.put(`/expert/bookings/${id}`, { status });
    toast.success(status === "accepted" ? "Réservation acceptée" : "Réservation refusée");
    load();
  };

  const pending = bookings.filter((b) => b.status === "pending");

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-navy flex items-center justify-center"><CalendarClock className="w-5 h-5 text-sky-400" /></div>
          <div>
            <h1 className="text-3xl font-display font-bold text-navy">Mon Agenda Expert</h1>
            <p className="text-slate-500">Publiez vos disponibilités et gérez les demandes de réservation</p>
          </div>
        </div>
        {expert && (
          <button onClick={() => setShowForm(true)} data-testid="new-slot-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sky-400 text-navy font-semibold hover:bg-sky-500 transition-colors">
            <Plus className="w-4 h-4" /> Publier un créneau
          </button>
        )}
      </div>

      {expert === null && (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500" data-testid="no-expert">
          <Inbox className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Aucun profil expert n'est lié à ce compte. Cette page est réservée aux comptes experts.
        </div>
      )}

      {expert && (
        <>
          {/* Booking requests */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="booking-requests">
            <h3 className="font-display font-semibold text-navy text-lg">Demandes de réservation {pending.length > 0 && <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">{pending.length}</span>}</h3>
            <div className="mt-4 divide-y divide-slate-100">
              {bookings.map((bk) => (
                <div key={bk.id} className="flex items-center justify-between py-3 gap-3" data-testid={`booking-${bk.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-navy">{bk.requester_name}</div>
                    <div className="text-xs text-slate-500 font-mono">{bk.date} · {bk.start_time}–{bk.end_time}</div>
                    {bk.message && <div className="text-xs text-slate-500 mt-0.5 italic truncate">« {bk.message} »</div>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {bk.status === "pending" ? (
                      <>
                        <button onClick={() => decide(bk.id, "accepted")} data-testid={`accept-${bk.id}`} className="p-2 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100" title="Accepter"><CheckCircle2 className="w-4 h-4" /></button>
                        <button onClick={() => decide(bk.id, "refused")} data-testid={`refuse-${bk.id}`} className="p-2 rounded-lg bg-rose-50 text-rose-500 hover:bg-rose-100" title="Refuser"><XCircle className="w-4 h-4" /></button>
                      </>
                    ) : (
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${BOOK_STATUS[bk.status]?.cls}`}>{BOOK_STATUS[bk.status]?.label}</span>
                    )}
                  </div>
                </div>
              ))}
              {bookings.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">Aucune demande pour le moment.</p>}
            </div>
          </div>

          {/* Slots */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="slots-list">
            <h3 className="font-display font-semibold text-navy text-lg">Mes créneaux</h3>
            <div className="mt-4 grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {slots.map((s) => (
                <div key={s.id} className="rounded-xl border border-slate-200 p-4" data-testid={`slot-${s.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2 text-navy font-semibold"><Clock className="w-4 h-4 text-sky-500" /> {s.start_time}–{s.end_time}</div>
                    <button onClick={() => remove(s.id)} className="p-1 rounded text-rose-400 hover:bg-rose-50" data-testid={`delete-slot-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="text-sm text-slate-500 font-mono mt-1">{s.date}</div>
                  {s.note && <div className="text-xs text-slate-500 mt-1">{s.note}</div>}
                  <span className={`inline-block mt-2 px-2.5 py-1 rounded-full text-xs font-semibold ${SLOT_STATUS[s.status]?.cls}`}>{SLOT_STATUS[s.status]?.label}</span>
                </div>
              ))}
              {slots.length === 0 && <p className="col-span-full text-sm text-slate-400 py-6 text-center">Aucun créneau publié.</p>}
            </div>
          </div>
        </>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="slot-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-display font-bold text-navy">Nouveau créneau</h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-500">Date</label>
                <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="slot-date"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500">Début</label>
                  <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="slot-start"
                    className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Fin</label>
                  <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="slot-end"
                    className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Note (optionnel)</label>
                <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Ex: conseil chiffrage" data-testid="slot-note"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400" />
              </div>
            </div>
            <button onClick={create} data-testid="save-slot"
              className="mt-5 w-full py-3 rounded-xl bg-navy text-white font-semibold hover:bg-navy-deep transition-colors">Publier</button>
          </div>
        </div>
      )}
    </div>
  );
}
